import { motion } from "framer-motion";
import { LineReveal, CTAButton, Reveal } from "@/components/okaia/Reveal";
import { TIDYCAL_URL, TALLY_URL } from "@/config/links";

const SITU_DIAG = [
  "Vous avez du mal à présenter clairement votre offre.",
  "Vous cherchez comment développer votre clientèle.",
  "Vous avez plusieurs priorités et ne savez pas par où commencer.",
];

const SITU_FORM = [
  "Vous souhaitez évoluer, mais ne savez pas quelle formation choisir.",
  "Vous hésitez entre plusieurs domaines ou programmes.",
  "Vous avez besoin d'y voir plus clair avant de vous engager.",
];

const goToCard = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) window.__lenis?.scrollTo(el, { offset: -90 });
};

export const Hero = () => (
  <section id="accueil" className="relative overflow-hidden bg-[#0B2215]">
    <div aria-hidden="true" className="absolute inset-0">
      <img src="/hero-bg.jpg" alt="" className="h-full w-full object-cover object-[75%_top] opacity-50" />
      <div className="absolute inset-0 bg-[#0B2215]/75" />
    </div>

    <div className="relative mx-auto w-full max-w-[1400px] px-6 pb-16 pt-28 md:px-12 md:pb-20 md:pt-36">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.1 }}
        className="okaia-overline mb-6 flex items-center gap-4 text-[#C8A97E]"
      >
        <span className="inline-block h-px w-12 flex-shrink-0 bg-[#C8A97E]" />
        <span className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
          <span className="font-serif-display text-sm normal-case tracking-normal">
            <span className="font-semibold text-[#C8A97E]">OKAÏA</span>{" "}
            <span className="italic text-[#F9F8F6]">Dév</span>
          </span>
          <span className="text-[10px] tracking-[0.22em] text-[#C8A97E]/90 sm:hidden">
            Pour les porteurs de projet, indépendants et entrepreneurs
          </span>
          <span className="hidden sm:inline">— Pour les porteurs de projet, indépendants et entrepreneurs</span>
        </span>
      </motion.p>

      <h1 className="max-w-4xl font-serif-display text-4xl leading-[1.1] tracking-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl">
        <LineReveal
          startDelay={0.2}
          lines={["Faire avancer votre entreprise", "ou trouver votre voie", "grâce à une formation"]}
        />
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
        data-testid="hero-intro"
        className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#A3B1AA] md:text-lg"
      >
        OKAÏA Dév vous aide à clarifier votre besoin et à choisir vos prochaines étapes.
        Sélectionnez le parcours qui correspond à votre situation.
      </motion.p>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.9 }}
        className="mt-6 flex flex-col gap-3 sm:hidden"
      >
        <a
          href="#offre-diagnostic"
          onClick={goToCard("offre-diagnostic")}
          data-testid="hero-quicklink-diagnostic"
          className="inline-flex w-fit items-center gap-2 border border-[#C8A97E] px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#C8A97E]"
        >
          Diagnostic Business — 49 € ↓
        </a>
        <a
          href="#offre-formation"
          onClick={goToCard("offre-formation")}
          data-testid="hero-quicklink-formation"
          className="inline-flex w-fit items-center gap-2 border border-[#F9F8F6]/50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#F9F8F6]"
        >
          Orientation formation — gratuite ↓
        </a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
        className="mt-10 grid gap-6 lg:grid-cols-2"
      >
        <div
          id="offre-diagnostic"
          data-testid="offer-diagnostic"
          style={{ scrollMarginTop: "100px" }}
          className="flex flex-col border border-[#C8A97E]/70 bg-[#F9F8F6]"
        >
          <img src="/illus-plan.jpg" alt="Illustration : plan d'action pour l'entreprise" className="h-24 w-full border-b border-[#C8A97E]/40 object-cover md:h-28" />
          <div className="flex flex-1 flex-col p-7 md:p-9">
            <p className="okaia-overline text-[#B39265]">Diagnostic Business OKAÏA Dév</p>
            <h2 className="mt-4 font-serif-display text-2xl leading-snug tracking-tight text-[#0B2215] md:text-3xl">
              Identifiez ce qui freine votre activité et choisissez vos prochaines actions
            </h2>
            <p className="mt-4 text-sm font-light leading-relaxed text-[#4A5A52]">
              Pour les indépendants, artisans, commerçants, dirigeants de TPE et créateurs
              d'entreprise, quel que soit leur secteur d'activité.
            </p>
            <ul className="mt-4 space-y-2">
              {SITU_DIAG.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm font-light text-[#0B2215]">
                  <span className="mt-1.5 inline-block h-1 w-4 flex-shrink-0 bg-[#C8A97E]" />
                  {s}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm font-light leading-relaxed text-[#4A5A52]">
              Un entretien individuel de 30 minutes pour faire le point sur votre situation,
              examiner votre principal blocage et définir une priorité.
            </p>
            <p className="mt-3 text-sm font-light leading-relaxed text-[#0B2215]">
              <span className="font-medium">Vous recevez :</span> une synthèse écrite et cinq
              actions adaptées à vos moyens — trois prioritaires et deux à engager ensuite.
            </p>
            <p data-testid="offer-diagnostic-tarif" className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#B39265]">
              49 € • 30 minutes • Visioconférence
            </p>
            <div className="mt-auto pt-6">
              <CTAButton label="Réserver mon diagnostic — 49 €" testId="offer-diagnostic-cta" href={TIDYCAL_URL} />
            </div>
          </div>
        </div>

        <div
          id="offre-formation"
          data-testid="offer-orientation"
          style={{ scrollMarginTop: "100px" }}
          className="flex flex-col border border-[#C8A97E]/70 bg-[#F0EBE1]"
        >
          <img src="/illus-formation.jpg" alt="Illustration : apprentissage et choix d'un parcours de formation" className="h-24 w-full border-b border-[#C8A97E]/40 object-cover md:h-28" />
          <div className="flex flex-1 flex-col p-7 md:p-9">
            <p className="okaia-overline text-[#B39265]">Orientation Formation OKAÏA Dév</p>
            <h2 className="mt-4 font-serif-display text-2xl leading-snug tracking-tight text-[#0B2215] md:text-3xl">
              Trouvez une formation adaptée à votre objectif professionnel
            </h2>
            <p className="mt-4 text-sm font-light leading-relaxed text-[#4A5A52]">
              Pour les personnes qui souhaitent apprendre un nouveau métier, préparer une
              reconversion ou développer des compétences utiles à leur activité.
            </p>
            <ul className="mt-4 space-y-2">
              {SITU_FORM.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm font-light text-[#0B2215]">
                  <span className="mt-1.5 inline-block h-1 w-4 flex-shrink-0 bg-[#C8A97E]" />
                  {s}
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm font-light leading-relaxed text-[#4A5A52]">
              Décrivez votre projet dans un court questionnaire. Léandre étudie votre demande
              pour préciser votre besoin et vous orienter vers des pistes de formation adaptées.
            </p>
            <p className="mt-3 text-sm font-light italic leading-relaxed text-[#4A5A52]">
              Un échange humain pour mieux comprendre les programmes, leurs prérequis et leurs
              conditions avant de faire votre choix.
            </p>
            <p data-testid="offer-orientation-gratuite" className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#B39265]">
              Orientation gratuite • Aucun diagnostic payant obligatoire
            </p>
            <div className="mt-auto pt-6">
              <CTAButton label="Trouver ma formation" testId="offer-orientation-cta" href={TALLY_URL} />
              <p data-testid="offer-orientation-mention" className="mt-3 text-xs font-light italic text-[#4A5A52]">
                L'orientation est gratuite. Les formations peuvent être payantes&nbsp;; leurs
                tarifs et possibilités de financement dépendent de chaque situation.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <Reveal delay={0.1}>
        <p className="mt-10 font-serif-display text-base italic text-[#C8A97E] md:text-lg">
          Voir clair. <span className="text-[#F9F8F6]">Décider juste.</span> Développer durablement.
        </p>
      </Reveal>
    </div>
  </section>
);
